import { formatShortDateWithWeekday } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardActionArea, CardContent, Stack } from "@mui/material";
import { BaseLink } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { formatDistanceInMiles } from "@src/appV2/lib/utils/distance";
import { WorkplaceNameWithDistance } from "@src/appV2/redesign/Shift/Open/WorkplaceNameWithDistance";
import { Card, CbhIcon, Text } from "@src/appV2/redesign/system/ui-components";

import { getPlacementDetailsPath } from "../../paths";
import { type PlacementData } from "../../types/placement";
import { PlacementWorkerTypePill } from "../PlacementCard/components/PlacementWorkerTypePill";
import { SentApplicationCardTitle } from "./components/SentApplicationCardTitle";

interface ApplicationPlacementCardProps {
  application: { appliedDate: string; updatedAt?: string };
  placement: Pick<PlacementData, "id" | "distance" | "workerTypes">;
  placementCandidateId: string;
  workplaceName: string;
  pageIndex: number;
  placementIndex: number;
  source: "booked_interviews" | "sent_applications" | "easy_scheduling_placements";
  workerTimeZone: string;
  interviewedDate?: string;
}

export function ApplicationPlacementCard(props: ApplicationPlacementCardProps) {
  const {
    application,
    placement,
    placementCandidateId,
    workplaceName,
    pageIndex,
    placementIndex,
    source,
    workerTimeZone,
  } = props;

  const { appliedDate, updatedAt } = application;
  const { id: placementId, distance } = placement;
  const { interviewedDate } = props;
  const autoOpenInterviewBooking = source === "easy_scheduling_placements";
  const detailsPath = getPlacementDetailsPath(placementId, placementCandidateId, {
    autoOpenInterviewBooking,
  });

  return (
    <Card
      variant="primary"
      sx={{
        border: (theme) => `1px solid ${theme.palette.border?.subtle}`,
        background: (theme) => theme.palette.background.quaternary,
      }}
    >
      <CardActionArea
        component={BaseLink}
        to={detailsPath}
        onClick={() => {
          logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_VIEWED, {
            placementCandidateId,
            placementDetails: placement,
            page: pageIndex + 1,
            listIndex: placementIndex + 1,
            source,
          });
        }}
      >
        <CardContent>
          <Stack spacing={5} sx={{ paddingTop: 2 }}>
            <Stack spacing={3}>
              <SentApplicationCardTitle
                appliedDate={appliedDate}
                workerTimezone={workerTimeZone}
                variant={source === "easy_scheduling_placements" ? "easy_scheduling" : "default"}
                updatedAt={updatedAt}
              />
              {isDefined(interviewedDate) && (
                <Text variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
                  Interviewed on{" "}
                  {formatShortDateWithWeekday(interviewedDate, {
                    timeZone: workerTimeZone,
                  })}
                </Text>
              )}
              <WorkplaceNameWithDistance
                textVariant="body1"
                workplaceName={workplaceName}
                formattedDistance={formatDistanceInMiles(distance ?? 0)}
              />
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                {isDefined(placement.workerTypes) && (
                  <PlacementWorkerTypePill workerTypes={placement.workerTypes} />
                )}
              </Box>
              <Box>
                {source === "easy_scheduling_placements" && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Text
                      variant="body2"
                      sx={(theme) => ({
                        color: theme.palette.error.main,
                        whiteSpace: "nowrap",
                        fontWeight: "bold",
                      })}
                    >
                      Schedule interview now
                    </Text>
                    <CbhIcon
                      type="chevron-right"
                      size="small"
                      sx={(theme) => ({
                        color: theme.palette.error.main,
                      })}
                    />
                  </Stack>
                )}
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
